import {useMemo} from 'react'
import {IconButton, Link, ActionMenu, ActionList, Truncate} from '@primer/react'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import type {IssueReference as IssueReferenceType} from '../../types/blackbird-types'
import {getIssueIconColor, getIssueIcon} from '../../utilities/issue-utils'
import styles from './IssueReferences.module.css'

export default function IssueReferences({issueRefs}: {issueRefs: IssueReferenceType[]}) {
  if (!issueRefs || issueRefs.length === 0) return <></>
  const [pullRequests, issues] = splitIssuesAndPRs(issueRefs)

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
        <IconButton
          unsafeDisableTooltip
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label="Open linked issues"
        />
      </ActionMenu.Anchor>

      <ActionMenu.Overlay className={styles.overlay}>
        <>
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          {issues!.length > 0 && (
            <ActionList>
              <ActionList.Group>
                <ActionList.GroupHeading>Linked issues</ActionList.GroupHeading>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                {issues!.map((issue, i) => {
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  return <IssueReference issue={issue} key={i} />
                })}
              </ActionList.Group>
            </ActionList>
          )}
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          {issues!.length > 0 && pullRequests!.length > 0 && <ActionList.Divider />}
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          {pullRequests!.length > 0 && (
            <ActionList className={styles.linkedPullRequestsList}>
              <ActionList.Group>
                <ActionList.GroupHeading>Linked pull requests</ActionList.GroupHeading>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                {pullRequests!.map((issue, i) => {
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  return <IssueReference issue={issue} key={i} />
                })}
              </ActionList.Group>
            </ActionList>
          )}
        </>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function IssueReference({issue}: {issue: IssueReferenceType}) {
  const iconColor = getIssueIconColor(
    issue.state,
    issue.is_pull_request,
    issue.merged,
    issue.reviewable_state,
    issue.state_reason,
  )
  const ResolvedIcon = useMemo(
    () => getIssueIcon(issue.state, issue.is_pull_request, issue.state_reason),
    [issue.state, issue.is_pull_request, issue.state_reason],
  )

  return (
    <ActionList.Item>
      <ActionList.LeadingVisual>
        <span style={{'--issueIconColor': iconColor}} className={styles.issueIcon}>
          {/* eslint-disable-next-line react-hooks/static-components -- dynamic icon resolved from getIssueIcon */}
          <ResolvedIcon size={16} />
        </span>
      </ActionList.LeadingVisual>
      <Truncate title={issue.title} maxWidth={250}>
        <Link href={issue.permalink} muted>
          {issue.title}
        </Link>
      </Truncate>
      <ActionList.TrailingVisual>#{issue.id}</ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function splitIssuesAndPRs(issueRefs: IssueReferenceType[]) {
  const pullRequests: IssueReferenceType[] = []
  const issues: IssueReferenceType[] = []
  for (const issue of issueRefs) {
    if (issue.is_pull_request) {
      pullRequests.push(issue)
    } else {
      issues.push(issue)
    }
  }
  return [pullRequests, issues]
}
